import { useState, useCallback, useEffect, useRef } from 'react';
import { QueryCondition } from '../lib/types';
import { useMap } from '../components/map/map-context';
import { layers } from '../geospatial';
import { combineQueryConditions } from '../lib/utils/cql-generator';
import TileWMS from 'ol/source/TileWMS';
import { storageManager } from '@/lib/utils/storage-manager';

export function useQuery() {
  const { map, currentTheme } = useMap();
  const debounceTimerRef = useRef<number | null>(null);
  
  function createEmptyCondition(): QueryCondition {
    return {
      attribute: '',
      value: '',
      logicalOperator: 'AND',
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      startParens: 0,
      endParens: 0,
    };
  }
  
  const [queryState, setQueryState] = useState<QueryCondition[]>(() => {
    const stored = storageManager.loadQueryState();
    if (stored && Array.isArray(stored) && stored.length > 0) {
      const normalized: QueryCondition[] = stored.map((c): QueryCondition => ({
        id: c.id ?? `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        attribute: c.attribute ?? '',
        value: Array.isArray(c.value) || typeof c.value === 'string' ? c.value : '',
        logicalOperator: c.logicalOperator === 'OR' ? 'OR' : 'AND',
        startParens: typeof c.startParens === 'number' ? c.startParens : 0,
        endParens: typeof c.endParens === 'number' ? c.endParens : 0,
      }));
      return normalized;
    }
    return [createEmptyCondition()];
  });

  // Clear persisted query on full page reload/close so it doesn't survive refreshes
  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        storageManager.clearQueryState();
      } catch {
        // ignore
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const addCondition = useCallback(
    () => {
      const newCondition: QueryCondition = createEmptyCondition();

      setQueryState((prev) => [...(prev || []), newCondition]);
    },
    [],
  );

  const removeCondition = useCallback((conditionId: string) => {
    setQueryState((prev) => (prev || []).filter((c) => c.id !== conditionId));
  }, []);

  const updateCondition = useCallback((conditionId: string, updates: Partial<QueryCondition>) => {
    setQueryState((prev) => (prev || []).map((c) => (c.id === conditionId ? { ...c, ...updates } : c)));
  }, []);

  const applyFilter = useCallback(async (): Promise<void> => {
    if (!map) {
      throw new Error('Map not available');
    }
    
    const validConditions = (queryState || []).filter(
      (c) => c.attribute && c.value && 
        (typeof c.value === 'string' ? c.value.length > 0 : Array.isArray(c.value) ? c.value.length > 0 : false)
    );

    if (validConditions.length === 0) {
      throw new Error('No valid query conditions to apply');
    }

    try {
      const cqlString = combineQueryConditions(validConditions);
      const layer = layers[0].layer;
      
      // Check if layer has getSource method (excludes LayerGroup)
      if ('getSource' in layer) { 
        const source = layer.getSource();
        
        if (source instanceof TileWMS) {
          source.updateParams({ cql_filter: cqlString });
          // Force a refresh of the layer
          source.refresh();
        } else {
          throw new Error('Layer source is not a TileWMS source');
        }
      } else {
        throw new Error('Layer does not have a source');
      }
    } catch (error) {
      console.error('Error applying filter:', error);
      throw error;
    }
  }, [map, queryState]);

  const clearFilter = useCallback(() => {
    if (!map) return;

    try {
      const layer = layers[0].layer;
      
      // Check if layer has getSource method (excludes LayerGroup)
      if ('getSource' in layer) {
        const source = layer.getSource();
        
        if (source instanceof TileWMS) {
          source.updateParams({ cql_filter: null });
          // Force a refresh of the layer
          source.refresh();
        }
      }
    } catch (error) {
      console.error('Error clearing filter:', error);
    }

    setQueryState([createEmptyCondition()]);
    storageManager.saveQueryState([createEmptyCondition()]);
  }, [map]);

  // Persist query state anytime it changes
  useEffect(() => {
    try {
      storageManager.saveQueryState(queryState);
    } catch {
      // ignore storage errors
    }
  }, [queryState]);

  useEffect(() => {
    // Debounced auto-apply whenever queryState changes
    if (!map) return;

    const hasValid = (queryState || []).some((c) => c.attribute && c.value && 
      (typeof c.value === 'string' ? c.value.length > 0 : Array.isArray(c.value) ? c.value.length > 0 : false));

    // Clear prior debounce
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // If no valid conditions, clear filter after small debounce
    if (!hasValid) {
      debounceTimerRef.current = window.setTimeout(() => {
        try {
          const layer = layers[0].layer;
          if ('getSource' in layer) {
            const source = layer.getSource();
            if (source instanceof TileWMS) {
              source.updateParams({ cql_filter: null });
              source.refresh();
            }
          }
        } catch (err) {
          console.error('Auto-clear filter failed:', err);
        }
      }, 300);
      return;
    }

    debounceTimerRef.current = window.setTimeout(() => {
      void applyFilter().catch((err) => {
        console.error('Auto-apply filter failed:', err);
      });
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [map, queryState, applyFilter, currentTheme]);

  return {
    queryState,
    addCondition,
    removeCondition,
    updateCondition,
    applyFilter,
    clearFilter,
  };
}
